import { Injectable } from '@angular/core';
import { Trailer } from 'interfaces/common/db/models/trailer';
import { AuthService } from '@services';
import { Subscription } from 'rxjs';
import { CacheListener } from './@utils/cache-listener';
import dexFreight from './microservices';
import type { RemoveManyTrailersResult } from 'interfaces/modules/trailer/http/removeMany.endpoint';
import type { ParsedTrailersCSV } from 'interfaces/modules/trailer/http/parseTrailersCSV.endpoint';
import type {
  CreateManyTrailersPayload, CreateManyTrailersResult,
} from 'interfaces/modules/trailer/http/createManyTrailers.endpoint';

/** Una entrada del catálogo `c-sub-tipo-rem` del SAT. */
export interface TrailerType {
  claveRemolque: string;
  remolque: string;
}

/**
 * Los remolques de la empresa, como flota propia.
 *
 * Vivían dentro de `vehicle.remolques[]` y por eso no tenían servicio: no había
 * nada que listar, dar de alta ni dar de baja por separado. Este es el gemelo de
 * `VehiclesService` y sigue su forma a propósito — misma caché, mismos verbos,
 * misma política de sincronización— para que las dos pestañas de la vista de
 * flota se comporten igual.
 *
 * El catálogo de subtipos se lee del módulo `Vehicles`, que es donde vive
 * (`getAllSubTipoRem`): es una lista global del SAT y no había razón para
 * duplicar el endpoint al separar la entidad.
 */
@Injectable({
  providedIn: 'root'
})
export class TrailersService {

  trailers: CacheListener<Trailer[]> = new CacheListener("trailers", [], this.syncTrailers.bind(this));
  trailerTypes: CacheListener<TrailerType[]> = new CacheListener("trailerTypes", [], this.syncTrailerTypes.bind(this));
  listeners: Subscription[] = [];

  constructor(
    private auth: AuthService
  ) { }

  async all() {
    return await this.trailers.get();
  }

  setupListeners() {
    this.listeners.push(
      this.trailers.syncWhenChanges(this.auth.token),
      this.trailerTypes.syncWhenChanges(this.auth.token)
    );
  }

  removeListeners() {
    this.listeners.forEach(listener => listener.unsubscribe());
    this.listeners = [];
  }

  async syncTrailers() {
    const token = await this.auth.token.get();
    if (!token) return [];

    const trailers = await dexFreight.Trailers.getAll(token);

    // El más reciente arriba, como en la flota de vehículos: quien acaba de dar
    // uno de alta lo busca al principio de la lista, no al final.
    return trailers.reverse();
  }

  async syncTrailerTypes() {
    const token = await this.auth.token.get();
    if (!token) return [];

    return await dexFreight.Vehicles.getAllSubTipoRem(token);
  }

  async createTrailer(data: { subType: string; plate: string }, { sync = true } = {}) {
    const token = await this.auth.token.get();
    if (!token) return;

    const trailer = await dexFreight.Trailers.create(data, token);
    if (sync) await this.trailers.sync();

    return trailer;
  }

  async updateTrailer(trailerId: string, data: Partial<{ subType: string; plate: string }>, { sync = true } = {}) {
    const token = await this.auth.token.get();
    if (!token) return;

    await dexFreight.Trailers.update(trailerId, data, token);
    if (sync) await this.trailers.sync();
  }

  async removeTrailer(trailerId: string, { sync = true } = {}) {
    const token = await this.auth.token.get();
    if (!token) return;

    await dexFreight.Trailers.remove(trailerId, token);
    if (sync) await this.trailers.sync();
  }

  /**
   * Baja en bloque. Un remolque asignado a una carga viva no se da de baja y
   * vuelve en `failed` con el motivo; los demás sí. Sincroniza una vez.
   */
  async removeTrailers(trailerIds: string[], { sync = true } = {}): Promise<RemoveManyTrailersResult> {
    const token = await this.auth.token.get();
    if (!token) return { removed: [], failed: [] };

    const result = await dexFreight.Trailers.removeMany(trailerIds, token) as RemoveManyTrailersResult;
    if (sync) await this.trailers.sync();

    return result;
  }

  // ── Carga masiva por CSV ────────────────────────────────────────────────
  //
  // Mismo reparto que la de vehículos: el backend lee el archivo (un solo
  // tokenizador de CSV en dexCarga) y el cliente coteja contra el catálogo.

  /** Lee el archivo y devuelve sus filas. No escribe nada. */
  async parseTrailersCSV(content: string): Promise<ParsedTrailersCSV> {
    const token = await this.auth.token.get();
    if (!token) return { headers: null, rows: [] };

    return await dexFreight.Trailers.parseTrailersCSV(content, token) as ParsedTrailersCSV;
  }

  /**
   * Alta por lote, máximo 200 por llamada. Nunca sobrescribe: una placa que ya
   * existe se aparta. No sincroniza: lo hace el llamador una vez al terminar.
   */
  async createManyTrailers(trailers: CreateManyTrailersPayload[]): Promise<CreateManyTrailersResult> {
    const token = await this.auth.token.get();

    return await dexFreight.Trailers.createManyTrailers(trailers, token) as CreateManyTrailersResult;
  }
}
